import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaShieldAlt, FaPlus, FaPen, FaArchive, FaKey, FaPaperclip, FaLock, FaExternalLinkAlt, FaStar, FaRegStar, FaSlidersH } from "react-icons/fa";
import { PERSONAL_VAULT_CONSTANTS } from "../_MODULES/_API";
import { PersonalVaultGroupFormModal } from "../_UI/PersonalVaultGroupFormModal";
import { PersonalVaultSecurityGuide } from "../_UI/PersonalVaultSecurityGuide";
import { OPTIONS_VAULT_KIND, onFindOptionLabel } from "../../../_Catalogs.Views";
import { FieldDate, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { usePagedList } from "../../../../_UI/usePagedList";
import "../_UI/_Styles/PersonalVault.css";

export const PersonalVaultViewMain = () => {
    const [useSearch, setSearch] = useState("");
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", kind: "ALL", pin: "ALL", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const [useGroupEdit, setGroupEdit] = useState(null);
    const [useIsModalOpen, setIsModalOpen] = useState(false);

    const { apiGet: onGetGroups, useApiData: GROUPS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    const onLoadGroups = () => onGetGroups({ endpoint: PERSONAL_VAULT_CONSTANTS.GROUP_LIST });

    useEffect(() => { onLoadGroups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onOpenCreate = () => {
        setGroupEdit(null);
        setIsModalOpen(true);
    };

    const onOpenEdit = (group) => {
        setGroupEdit(group);
        setIsModalOpen(true);
    };

    const onArchive = async (group) => {
        const IS_CONFIRMED = await confirm.danger(
            "Archivar grupo personal",
            `Se archiva "${group.name}" con sus accesos y archivos. Podés restaurarlo desde Archivados.`,
        );

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: PERSONAL_VAULT_CONSTANTS.GROUP_ARCHIVE,
            pathParams: { id: group.id }, successMessage: "Grupo archivado",
        });

        if (RESPONSE.success) mutateLocal({ id: group.id, remove: true });
    };

    const onToggleFavorite = async (group) => {
        const previous = !!group.isFavorite;
        const next = !previous;
        mutateLocal({ id: group.id, patch: { isFavorite: next } });

        const RESPONSE = await onSilentPatch({
            endpoint: PERSONAL_VAULT_CONSTANTS.GROUP_UPDATE,
            id: group.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del grupo.",
        });

        if (!RESPONSE.success) mutateLocal({ id: group.id, patch: { isFavorite: previous } });
    };

    const ITEMS = GROUPS ?? [];

    const VISIBLE = [...ITEMS]
        .filter((group) => advanced.favorite === "ALL" || (!!group.isFavorite === (advanced.favorite === "YES")))
        .filter((group) => advanced.kind === "ALL" || String(group.kind || "") === String(advanced.kind))
        .filter((group) => advanced.pin === "ALL" || (!!group.hasAccessPin === (advanced.pin === "YES")))
        .filter((group) => onDateMatches(advanced.dateField === "CREATED" ? group.createdAt : group.updatedAt, advanced.dateRange, advanced.dateExact))
        .filter((group) => onMatchesSearch(useSearch, [
            group.name, group.description, group.url, group.kind,
            ...(group.entries ?? []).flatMap((entry) => [entry.title, entry.email, entry.username, entry.host]),
        ]))
        .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || String(a.name ?? "").localeCompare(String(b.name ?? "")));

    const advancedCount = [advanced.favorite !== "ALL", advanced.kind !== "ALL", advanced.pin !== "ALL", advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ favorite: "ALL", kind: "ALL", pin: "ALL", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const EXPORT_COLUMNS = [
        { key: "name", label: "Grupo" },
        { key: "kind", label: "Tipo" },
        { key: "url", label: "Sitio" },
        { key: "description", label: "Descripción" },
        { key: "entries", label: "Accesos" },
        { key: "files", label: "Archivos" },
    ];

    const EXPORT_ROWS = VISIBLE.map((group) => ({
        name: group.name,
        kind: onFindOptionLabel(OPTIONS_VAULT_KIND, group.kind),
        url: group.url ?? "",
        description: group.description ?? "",
        entries: group.entries?.length ?? 0,
        files: group.files?.length ?? 0,
    }));

    const PAGER = usePagedList(VISIBLE, 6, "personalVaultGroupsPage");

    return (
        <ViewLayout
            header={
                <PageHeader
                    title="Bóveda personal"
                    description="Cuentas, contraseñas, licencias y archivos propios. Totalmente separada de compañías y proyectos."
                    icon={<FaShieldAlt />}
                    actions={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo grupo</AppButton>} />
            }
            toolbar={
                ITEMS.length > 0 && (
                    <ViewToolbar
                        filters={<>
                            <SearchFilter value={useSearch} onChange={setSearch}
                                total={ITEMS.length} matches={VISIBLE.length}
                                placeholder="Grupo, servicio o acceso personal" />
                            <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}</AppButton>
                        </>}
                        expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                            <FieldSelect id="pv_adv_favorite" label="Destacado" value={advanced.favorite} onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                            <FieldSelect id="pv_adv_kind" label="Tipo" value={advanced.kind} onChange={(event) => setAdvanced((current) => ({ ...current, kind: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, ...OPTIONS_VAULT_KIND]} isSearchable />
                            <FieldSelect id="pv_adv_pin" label="PIN" value={advanced.pin} onChange={(event) => setAdvanced((current) => ({ ...current, pin: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Con PIN" }, { value: "NO", label: "Sin PIN" }]} />
                            <FieldDate id="pv_adv_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                            <FieldDate id="pv_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                            <FieldDate id="pv_adv_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                        </AdvancedFilterPanel>}
                        total={PAGER.total}
                        perPage={PAGER.usePerPage}
                        onChangePerPage={PAGER.onChangePerPage}
                        exportColumns={EXPORT_COLUMNS}
                        exportRows={EXPORT_ROWS}
                        exportName="boveda-personal" />
                )
            } >

            <PersonalVaultSecurityGuide />

            <p className="LStudio__app_todos_hint">
                Esta bóveda pertenece a tu usuario. No necesita compañía, proyecto ni subproyecto.
                Podés usarla para correo personal, bancos, redes, suscripciones, licencias, hosting propio o cualquier acceso privado.
            </p>

            {IS_LOADING && <Skeleton variant="box" height={160} count={2} />}

            {!IS_LOADING && ITEMS.length > 0 && VISIBLE.length === 0 && (
                <EmptyState icon={<FaShieldAlt />} title="Sin coincidencias"
                    description={`Ningún grupo ni acceso coincide con "${useSearch}".`} />
            )}

            {!IS_LOADING && ITEMS.length === 0 && (
                <EmptyState icon={<FaShieldAlt />} title="Tu bóveda personal está vacía"
                    description="Creá un grupo para empezar: Google, Banco, Redes, Suscripciones, Licencias o el servicio que quieras."
                    action={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo grupo</AppButton>} />
            )}

            {!IS_LOADING && VISIBLE.length > 0 && (
                <div className="LStudio__app_vault_grid">
                    {PAGER.paged.map((group) => (
                        <Card key={group.id} className="LStudio__app_vault_card">
                            <div className="LStudio__app_vault_card_head">
                                <span className="LStudio__app_vault_card_dot"
                                    style={{ background: group.color || "var(--LS__app_priv_accent, #e83a52)" }} />

                                <Link to={`/personal-vault/${group.id}`}
                                    className="LStudio__app_vault_card_name">
                                    {group.name}
                                    {group.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacado" />}
                                </Link>

                                <Badge tone="neutral" size="sm">
                                    {onFindOptionLabel(OPTIONS_VAULT_KIND, group.kind)}
                                </Badge>

                                {group.hasAccessPin && <FaLock className="LStudio__app_vault_card_lock" title="Protegido con pin" />}

                                <CardActionMenu title="Acciones del grupo" items={[
                                    { key: "favorite", icon: group.isFavorite ? <FaStar /> : <FaRegStar />,
                                        label: group.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(group) },
                                    { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(group) },
                                    { divider: true },
                                    { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchive(group) },
                                ]} />
                            </div>

                            {group.description && <p className="LStudio__app_vault_card_desc">{group.description}</p>}

                            {group.url && (
                                <a href={group.url} target="_blank" rel="noreferrer"
                                    className="LStudio__app_vault_card_url">
                                    {group.url.replace(/^https?:\/\//, "")} <FaExternalLinkAlt />
                                </a>
                            )}

                            <div className="LStudio__app_vault_card_meta">
                                <span><FaKey /> {group.entries?.length ?? 0} accesos</span>
                                <span><FaPaperclip /> {group.files?.length ?? 0} archivos</span>
                            </div>

                            <div className="LStudio__app_vault_card_actions">
                                <AppButton size="sm" variant="soft" to={`/personal-vault/${group.id}`}>Abrir</AppButton>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <PersonalVaultGroupFormModal
                isOpen={useIsModalOpen}
                group={useGroupEdit}
                groups={ITEMS}
                onClose={() => setIsModalOpen(false)}
                onSaved={(saved, isEdit) => mutateLocal(isEdit
                    ? { id: saved.id, patch: saved }
                    : { insert: saved })} />
        </ViewLayout>
    );
};
